/**
 * Le Projet — page dédiée présentant le portail client BCL Telecom &
 * Énergie : mission, parcours en 12 étapes, suivi pipeline temps réel
 * et bénéfices mobiles. Design mobile-first (375px → desktop) : grilles
 * fluides, typographies clampées, illustrations stack en mobile.
 */
import {
  ArrowRight,
  Bell,
  Check,
  CheckCircle2,
  ClipboardList,
  Clock,
  CreditCard,
  Eye,
  FileSearch,
  FileText,
  Hammer,
  Headphones,
  HelpCircle,
  Lock,
  MapPin,
  PackageCheck,
  Phone,
  Send,
  ShieldCheck,
  Smartphone,
  Sparkles,
  UserCheck,
  Users,
  Wifi,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MarketingLayout } from '@/components/site/marketing-layout';

type Step = {
  num: number;
  title: string;
  description: string;
  icon: typeof FileText;
  group: 'Avant chantier' | 'Pendant le dossier' | 'Pendant les travaux';
};

const journey: Step[] = [
  {
    num: 1,
    title: 'Création du compte',
    description:
      'Inscription en moins de 2 minutes : identité, contact, type de client (particulier, pro, promoteur, syndic).',
    icon: UserCheck,
    group: 'Avant chantier',
  },
  {
    num: 2,
    title: 'Qualification de la demande',
    description:
      "Étude de raccordement, génie civil, réparation, prévisite, enfouissement, devis travaux divers — notre logique d'orientation adapte les questions à votre besoin.",
    icon: FileSearch,
    group: 'Avant chantier',
  },
  {
    num: 3,
    title: 'Personnalisation du projet',
    description:
      'Adresse, typologie de bâti, nombre de logements, types d\'unités, état neuf / rénovation, infrastructure existante.',
    icon: MapPin,
    group: 'Avant chantier',
  },
  {
    num: 4,
    title: 'Questions fibre',
    description:
      'Présence de fourreaux, chambre télécom, raccordement aérien ou souterrain, travaux de génie civil déjà réalisés.',
    icon: Wifi,
    group: 'Avant chantier',
  },
  {
    num: 5,
    title: 'Choix du pack',
    description:
      'Sérénité, Sérénité Premium, Essentiel ou Confort. Tarification claire selon votre profil et le périmètre.',
    icon: PackageCheck,
    group: 'Pendant le dossier',
  },
  {
    num: 6,
    title: 'Dépôt des documents',
    description:
      "Pièce d'identité, permis de construire, plans, photos, attestation d'adressage — téléversés depuis votre téléphone, stockés en sécurité.",
    icon: FileText,
    group: 'Pendant le dossier',
  },
  {
    num: 7,
    title: 'Validation du dossier',
    description:
      "Vérification BCL pièce à pièce avec retours motivés en cas de refus. Tout reste centralisé dans l'espace client.",
    icon: CheckCircle2,
    group: 'Pendant le dossier',
  },
  {
    num: 8,
    title: 'Paiement sécurisé',
    description:
      'Carte bancaire en un clic via FAM (paiement immédiat, facture automatique) ou virement bancaire (IBAN dédié, référence dossier).',
    icon: CreditCard,
    group: 'Pendant le dossier',
  },
  {
    num: 9,
    title: 'Prise en charge BCL',
    description:
      'Un référent est nommé sur votre dossier. Analyse technique, demandes opérateur, planning des interventions.',
    icon: Users,
    group: 'Pendant les travaux',
  },
  {
    num: 10,
    title: 'Prévisite & intervention',
    description:
      'Rendez-vous technicien planifié, créneau confirmé par notification, intervention réalisée et compte rendu mis en ligne.',
    icon: Wrench,
    group: 'Pendant les travaux',
  },
  {
    num: 11,
    title: 'Travaux & génie civil',
    description:
      'Exécution des travaux, coordination opérateurs, contrôles intermédiaires — chaque jalon est visible dans la timeline.',
    icon: Hammer,
    group: 'Pendant les travaux',
  },
  {
    num: 12,
    title: 'Réception & PV',
    description:
      'PV de réception signé, test de débit, raccordement final activé. Tous les documents sont archivés dans votre espace.',
    icon: ClipboardList,
    group: 'Pendant les travaux',
  },
];

const mobileBenefits = [
  {
    icon: Bell,
    title: 'Notifications en temps réel',
    description:
      'Document refusé, paiement validé, RDV planifié, rapport disponible — chaque alerte arrive par e-mail (et SMS si activé).',
  },
  {
    icon: Eye,
    title: 'Timeline lisible mobile',
    description:
      "Suivi visuel des 12 étapes du dossier avec dates de complétion et badges colorés. Optimisé pour s'utiliser d'une main.",
  },
  {
    icon: Smartphone,
    title: 'Téléversement depuis le téléphone',
    description:
      'Photo prise sur site, plan scanné, attestation : tout se charge en quelques secondes depuis votre mobile.',
  },
  {
    icon: Lock,
    title: 'Authentification sécurisée',
    description:
      "Connexion JWT chiffrée, session expirant après 7 jours d'inactivité, support OTP par e-mail (option).",
  },
  {
    icon: ShieldCheck,
    title: 'Conformité ARCEP & RGPD',
    description:
      'Stockage S3 chiffré, traçabilité complète des actions, accès restreint pièce à pièce selon le rôle.',
  },
  {
    icon: Headphones,
    title: 'Support 7j/7 pendant chantier',
    description:
      "Hotline accessible directement depuis l'app, contact admin via messagerie intégrée, RDV en moins de 4 h ouvrées.",
  },
];

const keyFigures = [
  { value: '2 min', label: "Création d'un compte" },
  { value: '12', label: 'Étapes de suivi' },
  { value: '24 h', label: 'Validation document' },
  { value: '100 %', label: 'Conformité ARCEP' },
];

const audiences = [
  {
    icon: Users,
    title: 'Particuliers',
    description:
      'Maison neuve ou rénovation : nous montons le dossier auprès des opérateurs et coordonnons le raccordement.',
  },
  {
    icon: Hammer,
    title: 'Promoteurs & aménageurs',
    description:
      'Lotissements, immeubles collectifs, programmes neufs : étude, génie civil, livraison clé en main.',
  },
  {
    icon: ShieldCheck,
    title: 'Copropriétés & syndics',
    description:
      "Conventions de raccordement, accompagnement à l'AG, supervision des opérateurs sur place.",
  },
  {
    icon: PackageCheck,
    title: 'Entreprises',
    description:
      'Liens dédiés, GTR contractuelle, supervision réseau — sur devis adapté à vos besoins.',
  },
];

const stepGroups: Step['group'][] = [
  'Avant chantier',
  'Pendant le dossier',
  'Pendant les travaux',
];

const groupColors: Record<Step['group'], string> = {
  'Avant chantier': 'bg-blue-50 text-bcl-blue border-bcl-blue/20',
  'Pendant le dossier': 'bg-teal-50 text-bcl-teal border-bcl-teal/20',
  'Pendant les travaux': 'bg-amber-50 text-amber-700 border-amber-200',
};

export function ProjetPage() {
  return (
    <MarketingLayout>
      {/* HERO — gradient + chiffres clés */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-bcl-grey/40 to-white">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-bcl-blue/20 to-transparent" />
        <div className="container px-4 mx-auto py-12 sm:py-16 lg:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-4">
              <Sparkles className="h-3.5 w-3.5" />
              Le portail BCL
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-balance">
              Suivez votre raccordement fibre depuis votre poche
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-600 mx-auto max-w-2xl">
              Un espace client complet pour qualifier votre demande, déposer
              vos documents, payer en toute sécurité et suivre l&apos;avancée
              de vos travaux en temps réel — pensé d&apos;abord pour mobile.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link to="/signup" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-bcl-blue hover:bg-bcl-blue/90"
                >
                  Créer mon compte
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/offres" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Voir nos offres
                </Button>
              </Link>
            </div>
          </div>

          {/* Chiffres clés : 2x2 mobile, 4x1 desktop */}
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto">
            {keyFigures.map((f) => (
              <Card key={f.label} className="text-center">
                <CardContent className="py-5 sm:py-6">
                  <p className="text-2xl sm:text-3xl font-bold text-bcl-blue">
                    {f.value}
                  </p>
                  <p className="mt-1 text-xs sm:text-sm text-gray-600">
                    {f.label}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-center max-w-6xl mx-auto">
            <div>
              <div className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-3">
                Notre mission
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-balance">
                Simplifier l&apos;adduction télécoms pour tous les porteurs
                de projets immobiliers
              </h2>
              <p className="text-gray-600 leading-relaxed">
                BCL Telecom &amp; Énergie est un bureau d&apos;études référencé
                ARCEP qui prend en charge l&apos;intégralité du parcours
                technique et administratif du raccordement fibre. Le portail
                vous permet de piloter votre projet à distance, sans appels
                interminables ni dossiers papier, depuis n&apos;importe quel
                appareil.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Un interlocuteur unique du dépôt au PV de réception',
                  'Tarification publique sans surprise',
                  'Conformité ARCEP et confidentialité des données',
                  'Disponibilité support 7j/7 pendant chantier',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-bcl-teal" />
                    <span className="text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Mock visuel mobile timeline */}
            <div className="relative">
              <div className="absolute inset-0 -z-10 bg-gradient-to-tr from-bcl-blue/10 via-bcl-teal/10 to-transparent blur-2xl" />
              <div className="mx-auto max-w-sm rounded-3xl border border-gray-200 bg-white shadow-xl overflow-hidden">
                <div className="bg-bcl-blue text-white p-4">
                  <p className="text-xs opacity-80">Dossier BCL-2026-0241</p>
                  <p className="font-semibold">Raccordement maison</p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-bcl-teal" />
                    <span className="text-xs">Travaux en cours · 75 %</span>
                  </div>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  {[
                    { label: 'Dossier validé', done: true },
                    { label: 'Paiement encaissé', done: true },
                    { label: 'Prévisite réalisée', done: true },
                    { label: 'Travaux en cours', done: false, current: true },
                    { label: 'PV de réception', done: false },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center gap-3">
                      <span
                        className={
                          s.done
                            ? 'inline-grid h-6 w-6 place-items-center rounded-full bg-green-500 text-white text-xs'
                            : s.current
                              ? 'inline-block h-6 w-6 rounded-full bg-amber-400 ring-4 ring-amber-100'
                              : 'inline-block h-6 w-6 rounded-full bg-gray-200'
                        }
                      >
                        {s.done ? <Check className="h-3 w-3" aria-hidden="true" /> : ''}
                      </span>
                      <span
                        className={
                          s.done ? 'text-gray-500 line-through' : 'font-medium'
                        }
                      >
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
                  <span>Dernière mise à jour il y a 12 min</span>
                  <Bell className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARCOURS 12 ÉTAPES */}
      <section className="py-12 sm:py-16 bg-bcl-grey/30">
        <div className="container px-4 mx-auto">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <div className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-3">
              Parcours en 12 étapes
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 text-balance">
              De l&apos;inscription au raccordement, chaque étape est
              accompagnée
            </h2>
            <p className="text-gray-600">
              Un pipeline structuré qui vous donne en permanence la
              visibilité sur l&apos;avancée de votre dossier.
            </p>
          </div>

          {stepGroups.map((group) => (
            <div key={group} className="mb-8 sm:mb-10">
              <div className="mb-4 flex items-center gap-2 sm:gap-3">
                <Badge
                  variant="outline"
                  className={`${groupColors[group]} text-xs uppercase tracking-wide px-3 py-1`}
                >
                  {group}
                </Badge>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {journey
                  .filter((s) => s.group === group)
                  .map((step) => (
                    <Card
                      key={step.num}
                      className="border-gray-100 hover:border-bcl-blue/40 hover:shadow-md transition-all"
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-bcl-blue/10 text-bcl-blue">
                            <step.icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 font-semibold">
                              Étape {step.num}
                            </p>
                            <h3 className="font-semibold mt-0.5 text-sm sm:text-base">
                              {step.title}
                            </h3>
                          </div>
                        </div>
                        <p className="mt-3 text-xs sm:text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BÉNÉFICES MOBILE */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-3">
              <Smartphone className="h-3.5 w-3.5" />
              Pensé mobile-first
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 text-balance">
              Conçu pour être consulté et piloté depuis votre téléphone
            </h2>
            <p className="text-gray-600">
              Suivez la progression de vos travaux où que vous soyez, sans
              installation d&apos;application.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {mobileBenefits.map((b) => (
              <div
                key={b.title}
                className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6 hover:shadow-md transition-shadow"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-bcl-blue/10 text-bcl-blue mb-3">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold mb-1 text-sm sm:text-base">
                  {b.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="py-12 sm:py-16 bg-bcl-grey/30">
        <div className="container px-4 mx-auto">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 text-balance">
              Un portail unique pour quatre publics
            </h2>
            <p className="text-gray-600">
              Le parcours s&apos;adapte automatiquement à votre profil pour
              ne vous poser que les questions utiles.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
            {audiences.map((a) => (
              <Card key={a.title}>
                <CardContent className="p-5 sm:p-6">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-bcl-blue/10 text-bcl-blue mb-3">
                    <a.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold mb-2">{a.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    {a.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SUPPORT & SLA */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container px-4 mx-auto max-w-5xl">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-2">
                <div className="p-6 sm:p-8 lg:p-10 bg-bcl-blue text-white">
                  <h2 className="text-xl sm:text-2xl font-bold mb-3 text-balance">
                    Un support dédié pendant toute la durée du chantier
                  </h2>
                  <p className="text-white/85 text-sm sm:text-base mb-6">
                    Une question technique, un planning à ajuster, un
                    document à corriger : nous restons joignables sur les
                    canaux qui vous arrangent.
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 shrink-0" />
                      <span>+33 1 00 00 00 00 · Lun-Ven 9h-18h</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Send className="h-4 w-4 shrink-0" />
                      <span>contact@bcl-telecom.fr · réponse &lt; 24 h</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 shrink-0" />
                      <span>Hotline 7j/7 active pendant chantier</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 sm:p-8 lg:p-10 bg-white">
                  <h3 className="font-semibold text-lg mb-4">
                    Nos engagements de service
                  </h3>
                  <ul className="space-y-4 text-sm">
                    {[
                      {
                        label: 'Qualification de votre demande',
                        value: 'Sous 24 h ouvrées',
                      },
                      {
                        label: 'Validation des documents',
                        value: 'Sous 24 h ouvrées',
                      },
                      {
                        label: 'Confirmation de paiement carte',
                        value: 'Immédiate',
                      },
                      {
                        label: 'Validation virement bancaire',
                        value: 'Sous 24 h ouvrées',
                      },
                      {
                        label: 'Rappel après ouverture de ticket',
                        value: 'Sous 4 h ouvrées',
                      },
                    ].map((item) => (
                      <li
                        key={item.label}
                        className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2 last:border-0 last:pb-0"
                      >
                        <span className="text-gray-700">{item.label}</span>
                        <span className="font-semibold text-bcl-blue text-right shrink-0">
                          {item.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="py-12 sm:py-16 bg-bcl-grey/30">
        <div className="container px-4 mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-balance">
              Vous avez encore des questions&nbsp;?
            </h2>
            <p className="text-gray-600">
              Consultez notre FAQ ou contactez notre équipe pour un
              accompagnement personnalisé.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/faq" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                <HelpCircle className="mr-2 h-4 w-4" />
                Voir la FAQ complète
              </Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-bcl-blue hover:bg-bcl-blue/90"
              >
                Contacter un conseiller
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container px-4 mx-auto max-w-5xl">
          <div className="rounded-2xl bg-bcl-blue p-6 sm:p-10 text-white text-center overflow-hidden relative">
            <div className="absolute right-0 top-0 h-full w-1/3 bg-bcl-teal mix-blend-multiply opacity-70 hidden sm:block" />
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-balance">
                Démarrez votre projet en quelques minutes
              </h2>
              <p className="text-white/85 max-w-xl mx-auto mb-6 text-sm sm:text-base">
                Création de compte gratuite, qualification offerte, devis sous
                24 h ouvrées. Aucun engagement avant signature.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/signup" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="w-full sm:w-auto"
                  >
                    Créer mon compte
                  </Button>
                </Link>
                <Link to="/offres" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto bg-transparent border-white text-white hover:bg-white/10"
                  >
                    Voir les packs
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
